"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { AlertCircle, Building2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { exchangeGoogleOttAction } from "@/lib/api/google-actions";

const GOOGLE_ERRORS: Record<string, string> = {
  disabled: "Logowanie przez Google jest niedostępne.",
  state: "Sesja logowania wygasła — spróbuj ponownie.",
  expired: "Sesja logowania wygasła — spróbuj ponownie.",
  unverified:
    "Twoje konto Google nie ma zweryfikowanego adresu e-mail. Zweryfikuj adres w Google lub zaloguj się hasłem.",
  suspended: "Konto jest zawieszone. Skontaktuj się z administratorem.",
  signup_disabled: "Rejestracja jest obecnie niedostępna.",
  generic: "Logowanie przez Google nie powiodło się. Spróbuj ponownie.",
};

/**
 * Client-side tail of the Google flow on /login:
 * - `ott`      -> exchange immediately (the URL comes from a live browser
 *                 redirect, never from an e-mail — no prefetch hazard);
 * - `pickOtt`  -> company picker, exchange with the chosen slug;
 * - `errorCode`-> Polish message banner.
 */
export function GoogleOttHandler({
  ott,
  pickOtt,
  pickTenants,
  errorCode,
}: {
  ott?: string;
  pickOtt?: string;
  pickTenants?: Array<{ slug: string; name: string }>;
  errorCode?: string;
}) {
  const [error, setError] = useState<string | null>(
    errorCode ? (GOOGLE_ERRORS[errorCode] ?? GOOGLE_ERRORS.generic) : null,
  );
  const [pending, startTransition] = useTransition();

  // The OTT is single-use — StrictMode's dev double-effect (and any remount)
  // must not burn it twice and turn a successful login into a bogus error.
  const exchanged = useRef(false);
  useEffect(() => {
    if (!ott || exchanged.current) return;
    exchanged.current = true;
    // Success redirects server-side (NEXT_REDIRECT); only errors return.
    startTransition(async () => {
      const res = await exchangeGoogleOttAction(ott);
      setError(res.error);
    });
  }, [ott]);

  function pick(slug: string) {
    if (!pickOtt) return;
    setError(null);
    startTransition(async () => {
      const res = await exchangeGoogleOttAction(pickOtt, slug);
      setError(res.error);
    });
  }

  if (ott && !error) {
    return (
      <div
        role="status"
        className="flex items-center gap-2.5 rounded-xl border border-border bg-muted/40 px-3.5 py-3 text-sm text-muted-foreground"
      >
        <Loader2 className="size-4 animate-spin" />
        Logowanie przez Google…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {pickOtt && pickTenants?.length ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Twój adres e-mail występuje w kilku firmach. Wybierz, do której
            chcesz się zalogować:
          </p>
          {pickTenants.map((t) => (
            <Button
              key={t.slug}
              type="button"
              variant="outline"
              disabled={pending}
              onClick={() => pick(t.slug)}
              className="h-11 w-full justify-start gap-2"
            >
              {pending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Building2 className="size-4" />
              )}
              <span className="truncate">{t.name}</span>
              <span className="ml-auto font-mono text-xs text-muted-foreground">
                {t.slug}
              </span>
            </Button>
          ))}
        </div>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="flex items-start gap-2.5 rounded-xl border border-destructive/20 bg-destructive/8 px-3.5 py-3 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}
      {ott && error ? (
        // The page hides LoginForm while ?ott= is in the URL — after a failed
        // exchange this full-reload link is the way back to a clean /login.
        <Button
          render={<a href="/login" />}
          variant="outline"
          className="h-11 w-full"
        >
          Wróć do logowania
        </Button>
      ) : null}
    </div>
  );
}
